/**
 * Turn the maneuver tables' loss columns into "how far off the baseline was this?" columns while a
 * baseline is selected.
 *
 * Two swaps, both against the baseline's fitted value at the row's OWN wind speed:
 *   mmg                              -> BASELINE LOSS [M]     baseline - row  (positive = fell short)
 *   loss_inv/turn/build/total_tgt    -> BL …/TOTAL LOSS        row - baseline  (positive = lost more)
 *
 * The total goes over with the three phases so the four loss columns all read the same way and still
 * roughly add up; leaving it absolute put three deltas beside a total they no longer summed to.
 *
 * MMG is inverted because more metres made good is better, while a loss is worse -- so in both
 * families a positive number means "worse than the reference", which is the only way a table of
 * mixed columns can be read at a glance.
 *
 * The deltas are written onto the row objects as real numeric fields rather than computed at cell
 * render time, so sorting, conditional formatting and the grouped tables' averaging all keep
 * working with no knowledge of baselines.
 */

import type { ManeuverBaselineSeriesRow } from './maneuverBaselineFit';
import { seriesAtTws, readSeriesValue } from './maneuverBaselineFit';

/** Channel a table column reads -> the derived field that replaces it, and how to compute it. */
interface LossSwap {
  channel: string;
  derived: string;
  header: string;
  description: string;
  /** true: baseline - row (bigger is better); false: row - baseline (smaller is better). */
  invert: boolean;
}

const MMG_SWAP: LossSwap = {
  channel: 'mmg',
  derived: 'mmg_baseline_loss',
  header: 'BASELINE LOSS [M]',
  description: 'BASELINE MMG MINUS THIS MANEUVER — POSITIVE MEANS BELOW THE BASELINE',
  invert: true,
};

const PHASE_SWAPS: LossSwap[] = [
  {
    channel: 'loss_inv_tgt',
    derived: 'inv_baseline_loss',
    header: 'BL INV LOSS [M]',
    description: 'INVESTMENT LOSS MINUS THE BASELINE — POSITIVE MEANS MORE LOSS THAN THE BASELINE',
    invert: false,
  },
  {
    channel: 'loss_turn_tgt',
    derived: 'turn_baseline_loss',
    header: 'BL TURN LOSS [M]',
    description: 'TURN LOSS MINUS THE BASELINE — POSITIVE MEANS MORE LOSS THAN THE BASELINE',
    invert: false,
  },
  {
    channel: 'loss_build_tgt',
    derived: 'build_baseline_loss',
    header: 'BL BUILD LOSS [M]',
    description: 'BUILD LOSS MINUS THE BASELINE — POSITIVE MEANS MORE LOSS THAN THE BASELINE',
    invert: false,
  },
  {
    channel: 'loss_total_tgt',
    derived: 'total_baseline_loss',
    header: 'BL TOTAL LOSS [M]',
    description: 'TOTAL LOSS MINUS THE BASELINE — POSITIVE MEANS MORE LOSS THAN THE BASELINE',
    invert: false,
  },
];

/**
 * Every derived field this module can write.
 *
 * The maneuver scatters derive their chart list from the keys of the first row, so these names must
 * also be excluded there or each one spawns a phantom chart.
 */
export const MANEUVER_BASELINE_DERIVED_CHANNELS: readonly string[] = [
  MMG_SWAP.derived,
  ...PHASE_SWAPS.map((s) => s.derived),
];

export interface TableColumnConfigLike {
  columns: string[];
  descriptions: string[];
  channels: string[];
}

/**
 * Whether the selected baseline can support the phase-loss swap.
 *
 * Every phase AND the total must be fitted, or the table would show some columns against the
 * baseline and others against target — a row of numbers that no longer add up.
 */
export function baselineCoversPhaseLosses(
  series: ManeuverBaselineSeriesRow[] | null | undefined
): boolean {
  if (!series || series.length === 0) { return false; }
  return PHASE_SWAPS.every((swap) => series.some((row) => readSeriesValue(row, swap.channel) != null));
}

/**
 * Relabel the loss columns a table shows, leaving every other column untouched.
 *
 * `active` false returns the config unchanged (same object), so a page with no baseline selected
 * pays nothing and looks exactly as it did.
 */
export function withManeuverBaselineLossColumns<T extends TableColumnConfigLike>(
  config: T,
  options: { active: boolean; phaseCapable: boolean }
): T {
  if (!options.active) { return config; }

  const swaps = options.phaseCapable ? [MMG_SWAP, ...PHASE_SWAPS] : [MMG_SWAP];
  const byChannel = new Map(swaps.map((s) => [s.channel, s]));

  const columns = [...config.columns];
  const descriptions = [...config.descriptions];
  const channels = [...config.channels];
  let changed = false;

  for (let i = 0; i < channels.length; i++) {
    const swap = byChannel.get(String(channels[i]).toLowerCase());
    if (!swap) { continue; }
    columns[i] = swap.header;
    descriptions[i] = swap.description;
    channels[i] = swap.derived;
    changed = true;
  }

  return changed ? { ...config, columns, descriptions, channels } : config;
}

/**
 * Write (or clear) the derived delta fields on a set of maneuver rows.
 *
 * Mutates in place, which is what lets the existing sort / heat-map / grouped-average code see the
 * fields without being told about them. Called with `series` null when no baseline is selected, and
 * then it removes the fields — otherwise a stale delta would keep sorting a column that no longer
 * exists.
 */
export function applyManeuverBaselineLoss(
  rows: Array<Record<string, unknown>> | null | undefined,
  series: ManeuverBaselineSeriesRow[] | null | undefined,
  options: { phaseCapable: boolean } = { phaseCapable: false }
): void {
  if (!rows || rows.length === 0) { return; }
  const swaps = series
    ? (options.phaseCapable ? [MMG_SWAP, ...PHASE_SWAPS] : [MMG_SWAP])
    : [];

  for (const row of rows) {
    if (swaps.length === 0) {
      for (const field of MANEUVER_BASELINE_DERIVED_CHANNELS) { delete row[field]; }
      continue;
    }

    const tws = Number(row.tws_avg ?? row.Tws_avg);
    const reference = Number.isFinite(tws) ? seriesAtTws(series, tws) : null;

    for (const field of MANEUVER_BASELINE_DERIVED_CHANNELS) { delete row[field]; }
    if (!reference) { continue; }

    for (const swap of swaps) {
      const baselineValue = readSeriesValue(reference, swap.channel);
      const rowValueRaw = row[swap.channel] ?? row[swap.channel.charAt(0).toUpperCase() + swap.channel.slice(1)];
      const rowValue = Number(rowValueRaw);
      if (baselineValue == null || !Number.isFinite(rowValue)) { continue; }
      const delta = swap.invert ? baselineValue - rowValue : rowValue - baselineValue;
      // -0 prints as "-0" and reads as a real deficit; it is not one.
      row[swap.derived] = Math.round(delta * 1000) / 1000 || 0;
    }
  }
}

/** The pseudo event id of the pinned BASELINE row. Negative so it can never collide with a real one. */
export const MANEUVER_BASELINE_ROW_EVENT_ID = -880001;

export function isManeuverBaselineRow(row: { event_id?: unknown } | null | undefined): boolean {
  return Number(row?.event_id) === MANEUVER_BASELINE_ROW_EVENT_ID;
}

/**
 * A single pinned row holding the baseline's values at the selected wind speed.
 *
 * Every grouping/identity field reads "BASELINE" so the row is unmistakable in a sorted or grouped
 * table, and its own loss deltas are zero by definition (it IS the reference).
 */
export function buildManeuverBaselineTableRow(
  series: ManeuverBaselineSeriesRow[] | null | undefined,
  tws: number | null,
  options: { phaseCapable: boolean } = { phaseCapable: false }
): Record<string, unknown> | null {
  if (!series || series.length === 0 || tws == null || !Number.isFinite(tws)) { return null; }
  const reference = seriesAtTws(series, tws);
  if (!reference) { return null; }

  const row: Record<string, unknown> = {
    ...reference,
    event_id: MANEUVER_BASELINE_ROW_EVENT_ID,
    tws_avg: tws,
    tack: 'BASELINE',
    source_name: 'BASELINE',
    Source: 'BASELINE',
    Config: 'BASELINE',
    State: 'BASELINE',
    Year: 'BASELINE',
    Event: 'BASELINE',
    Race_number: 'BASELINE',
    datetime: null,
    datetimeLocal: 'BASELINE',
    // Keeps the tack-label logic in the tables from rewriting `tack` to PORT/STBD.
    twa_entry: -1,
  };

  const swaps = options.phaseCapable ? [MMG_SWAP, ...PHASE_SWAPS] : [MMG_SWAP];
  for (const swap of swaps) { row[swap.derived] = 0; }
  return row;
}

/** Prepend the pinned baseline row for rendering only — never write it into the data store. */
export function prependManeuverBaselineRow<T>(
  rows: T[],
  baselineRow: Record<string, unknown> | null
): Array<T | Record<string, unknown>> {
  return baselineRow ? [baselineRow, ...rows] : rows;
}
